import { supabase } from './supabaseClient';
import { getNegocioId } from './negocioActual';
import { actualizarProducto } from './productosService';

const BUCKET = 'ai-generaciones';

// Pide al backend generar una versión mejorada de una foto del producto.
// El token de sesión va en el header para que el servidor valide identidad,
// propiedad y crédito. La API key del proveedor vive SOLO en el servidor.
// Con `productoId`: mejora una foto de un producto guardado.
// Con `imagenUrl` (+ `datosProducto`) y sin productoId: mejora una foto SUELTA
// (al crear un producto nuevo, antes de guardarlo).
export async function generarImagen({ productoId, imagenOrigenIdx, estilo, instrucciones, aspecto, imagenUrl, datosProducto }) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) { const e = new Error('Inicia sesión para usar la IA.'); e.code = 'NO_AUTENTICADO'; throw e; }

  let resp;
  try {
    resp = await fetch('/api/generar-imagen', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ productoId, imagenOrigenIdx, estilo, instrucciones, aspecto, imagenUrl, datosProducto }),
    });
  } catch {
    const e = new Error('No se pudo conectar con el servidor de IA.'); e.code = 'SIN_CONEXION'; throw e;
  }
  const data = await resp.json().catch(() => ({}));
  if (data?.error) { const e = new Error(data.mensaje || 'No se pudo generar la imagen.'); e.code = data.error; throw e; }
  if (!resp.ok || !data?.ok) { const e = new Error(data?.mensaje || 'No se pudo generar la imagen.'); e.code = data?.error || 'ERROR'; throw e; }
  return data.generacion; // fila de ai_generaciones (estado 'completed' o 'processing')
}

// Consulta el estado de una generación asíncrona (proveedores lentos como Kie).
// Devuelve { ok?, procesando?, error?, mensaje?, generacion }.
export async function consultarEstado(generacionId) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) { const e = new Error('Inicia sesión.'); e.code = 'NO_AUTENTICADO'; throw e; }
  let resp;
  try {
    resp = await fetch('/api/ia-estado', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ generacionId }),
    });
  } catch { const e = new Error('No se pudo conectar con el servidor.'); e.code = 'SIN_CONEXION'; throw e; }
  return resp.json().catch(() => ({}));
}

// Avisa que la app dejó de esperar una generación. El servidor NO reembolsa
// mientras el proveedor sigue trabajando (la tarea ya se cobra): la deja en
// proceso para entregarla después; solo reembolsa si falla o se atasca.
export async function cancelarGeneracion(generacionId) {
  if (!generacionId) return null;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return null;
    const resp = await fetch('/api/ia-estado', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ generacionId, cancelar: true }),
    });
    return resp.json().catch(() => null);
  } catch { return null; }
}

// Historial de generaciones de un producto (RLS lo limita al negocio propio).
export async function listarGeneraciones(productoId) {
  const { data, error } = await supabase
    .from('ai_generaciones')
    .select('*')
    .eq('negocio_id', getNegocioId())
    .eq('producto_id', productoId)
    .order('creado_en', { ascending: false });
  if (error) throw error;
  return data || [];
}

// Consumo del mes (para mostrar "te quedan X" — opcional en la UI).
export async function obtenerCreditos() {
  const { data, error } = await supabase
    .from('ai_creditos').select('*').eq('negocio_id', getNegocioId()).maybeSingle();
  if (error) throw error;
  return data;
}

// Período de consumo actual en la zona horaria del negocio (Honduras).
// Debe coincidir con el que usa la función SQL reservar_credito_ia.
function periodoActualHN() {
  try {
    const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Tegucigalpa', year: 'numeric', month: '2-digit' }).formatToParts(new Date());
    const y = p.find((x) => x.type === 'year')?.value;
    const m = p.find((x) => x.type === 'month')?.value;
    if (y && m) return `${y}-${m}`;
  } catch { /* usa el respaldo UTC */ }
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

// Detalle de créditos para la UI: plan, usados, límite y cuántas fotos quedan
// este mes. Si aún no hay fila (negocio nuevo) o el período cambió, el consumo
// arranca en 0 (se reinicia solo en la próxima generación).
export async function obtenerCreditosDetalle() {
  const negocioId = getNegocioId();
  const { data: fila } = await supabase
    .from('ai_creditos').select('plan, periodo_ym, creditos_usados')
    .eq('negocio_id', negocioId).maybeSingle();
  const plan = fila?.plan || 'FREE';
  const periodo = periodoActualHN();
  const usados = fila && fila.periodo_ym === periodo ? (fila.creditos_usados || 0) : 0;

  const { data: planRow } = await supabase
    .from('planes').select('limite_generaciones_mes, nombre').eq('clave', plan).maybeSingle();
  const limite = planRow?.limite_generaciones_mes ?? 0;
  const restantes = Math.max(0, limite - usados);
  return { plan, planNombre: planRow?.nombre || plan, usados, limite, restantes, periodo };
}

// Elimina una generación: borra la fila (RLS) y su archivo del storage (best-effort).
export async function eliminarGeneracion(generacion) {
  const { error } = await supabase
    .from('ai_generaciones').delete()
    .eq('id', generacion.id).eq('negocio_id', getNegocioId());
  if (error) throw error;
  const marcador = `/object/public/${BUCKET}/`;
  if (generacion.resultado_url && generacion.resultado_url.includes(marcador)) {
    const ruta = decodeURIComponent(generacion.resultado_url.split(marcador)[1]);
    await supabase.storage.from(BUCKET).remove([ruta]).catch(() => {});
  }
}

// Guarda la imagen generada como una foto del producto.
// - Sin `slot`: la coloca en el primer espacio libre (foto_1..foto_5). Si las 5
//   están ocupadas devuelve { ok:false, motivo:'SIN_ESPACIO' } (la UI ofrece elegir).
// - Con `slot` (1..5): la coloca en ESE espacio (reemplaza el que elija el usuario).
// Si comoPrincipal, además la marca como principal. La generada nunca se pierde
// (vive en el historial). Devuelve { ok, slot } o { ok:false, motivo }.
export async function guardarComoFoto(producto, urlGenerada, { comoPrincipal = false, slot = null } = {}) {
  let destino = slot;
  if (!destino) {
    for (let i = 1; i <= 5; i++) { if (!producto[`foto_${i}_url`]) { destino = i; break; } }
  }
  if (!destino) return { ok: false, motivo: 'SIN_ESPACIO' }; // las 5 fotos están ocupadas
  const cambios = { [`foto_${destino}_url`]: urlGenerada };
  if (comoPrincipal) cambios.foto_principal = destino;
  await actualizarProducto(producto.id, cambios);
  return { ok: true, slot: destino };
}
